import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { 
    GitBranch, 
    Plus, 
    Trash2, 
    Info, 
    User, 
    Bot,
    Server, 
    Zap, 
    Globe2, 
    AlertCircle, 
    Loader2,
    MessageSquare,
    Clock,
    Hash,
    Lightbulb,
    Key
} from "lucide-react";
import { HandoffDestination, HandoffDestinationAssistant, HandoffDestinationDynamic, ContextEngineeringPlan } from "@/api/tools/tools.types";
import { getAssistants } from "@/api/assistants/assistants.api";
import type { Assistant } from "@/api/assistants/assistants.types";
import {
    Card,
    CardHeader,
    CardTitle,
    CardDescription,
    CardContent,
} from "@/components/ui/card";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import { SwitchWithLabel } from "@/components/ui/switch-with-label";
import { Switch } from "@/components/ui/switch";
import { Combobox } from "@/components/ui/combobox";
import { NumberInput } from "@/components/ui/number-input";

interface HandoffFormProps {
    value: HandoffDestination[] | null;
    onChange: (data: HandoffDestination[] | null) => void;
    isAsync?: boolean;
    onAsyncChange?: (isAsync: boolean) => void;
    isReadOnly?: boolean;
}

export function HandoffForm({ value, onChange, isAsync = false, onAsyncChange, isReadOnly = false }: HandoffFormProps) {
    const [destinations, setDestinations] = useState<HandoffDestination[]>(value || []);
    const [assistants, setAssistants] = useState<Assistant[]>([]);
    const [isLoadingAssistants, setIsLoadingAssistants] = useState(true);
    const [openAccordion, setOpenAccordion] = useState<string[]>([]);
    
    // Fetch assistants on mount
    useEffect(() => {
        const loadAssistants = async () => {
            try {
                const response = await getAssistants();
                if (response.data) {
                    setAssistants(response.data);
                }
            } catch (error) {
                console.error('Failed to load assistants:', error);
            } finally {
                setIsLoadingAssistants(false);
            }
        };
        loadAssistants();
    }, []);
    
    // Validate if URL matches the required format
    const isValidWorkflowUrl = (url: string): boolean => {
        if (!url) return true; // Empty is valid (will be caught by required validation)
        return url.startsWith('https://workflow.voicebot.studio/');
    };

    // Memoize the update function
    const updateParent = useCallback((newData: HandoffDestination[]) => {
        const isEqual = JSON.stringify(newData) === JSON.stringify(value);
        if (!isEqual) {
            onChange(newData);
        }
    }, [value, onChange]);

    // Sync local state with prop changes
    useEffect(() => {
        const isEqual = JSON.stringify(destinations) === JSON.stringify(value);
        if (!isEqual && value !== null) {
            setDestinations(value || []);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    const addDestination = () => {
        const newDestination: HandoffDestinationAssistant = {
            type: 'assistant',
            description: '',
            contextEngineeringPlan: {
                type: 'all'
            }
        };
        const newDestinations = [...destinations, newDestination];
        setDestinations(newDestinations);
        updateParent(newDestinations);
        // Collapse all existing destinations and open only the new one
        setOpenAccordion([`dest-${newDestinations.length - 1}`]);
    };

    const removeDestination = (index: number) => {
        const newDestinations = destinations.filter((_, i) => i !== index);
        setDestinations(newDestinations);
        updateParent(newDestinations);
    };

    const updateDestination = (index: number, updates: Partial<HandoffDestination>) => {
        const newDestinations = [...destinations];
        newDestinations[index] = { ...newDestinations[index], ...updates };
        setDestinations(newDestinations);
        updateParent(newDestinations);
    };

    const updateDestinationType = (index: number, newType: 'assistant' | 'dynamic') => {
        const currentDest = destinations[index];
        
        let newDestination: HandoffDestination;
        
        if (newType === 'assistant') {
            newDestination = {
                type: 'assistant',
                description: currentDest.description || '',
                contextEngineeringPlan: {
                    type: 'all'
                }
            };
        } else {
            newDestination = {
                type: 'dynamic',
                description: currentDest.description || '',
                server: {
                    url: '',
                    timeoutSeconds: 20
                }
            };
        }
        
        const newDestinations = [...destinations];
        newDestinations[index] = newDestination;
        setDestinations(newDestinations);
        updateParent(newDestinations);
    };

    const updateContextPlan = (index: number, planType: 'all' | 'lastNMessages' | 'none', maxMessages?: number) => {
        const dest = destinations[index];
        if (dest.type !== 'assistant') return;

        let newPlan: ContextEngineeringPlan;
        
        if (planType === 'lastNMessages') {
            newPlan = {
                type: 'lastNMessages',
                maxMessages: maxMessages || 10
            };
        } else {
            newPlan = { type: planType };
        }

        updateDestination(index, {
            contextEngineeringPlan: newPlan
        });
    };

    const updateServerField = (index: number, field: string, value: string | number) => {
        const dest = destinations[index];
        if (dest.type !== 'dynamic') return;

        const newServer = {
            ...dest.server,
            [field]: value
        };

        updateDestination(index, { server: newServer });
    };

    const updateServerHeader = (index: number, key: string, value: string) => {
        const dest = destinations[index];
        if (dest.type !== 'dynamic' || !dest.server) return;

        const newHeaders = {
            ...dest.server.headers,
            [key]: value
        };

        updateDestination(index, {
            server: {
                ...dest.server,
                headers: newHeaders
            }
        });
    };

    const removeServerHeader = (index: number, key: string) => {
        const dest = destinations[index];
        if (dest.type !== 'dynamic' || !dest.server?.headers) return;

        const newHeaders = { ...dest.server.headers };
        delete newHeaders[key];

        updateDestination(index, {
            server: {
                ...dest.server,
                headers: newHeaders
            }
        });
    };

    // Prepare assistant options for combobox
    const assistantOptions = assistants.map(assistant => ({
        value: assistant.id,
        label: assistant.name,
        description: assistant.id
    }));

    // Prepare destination type options
    const destinationTypeOptions = [
        { value: 'assistant', label: 'Assistant' },
        { value: 'dynamic', label: 'Dynamic' }
    ];

    // Prepare context plan options
    const contextPlanOptions = [
        { value: 'all', label: 'All Messages' },
        { value: 'lastNMessages', label: 'Last N Messages' },
        { value: 'none', label: 'No Messages' }
    ];

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.15 }}
            className="space-y-6"
        >
            {/* TransAssist Configuration Card */}
            <Card className="shadow-sm">
                <CardHeader className="border-b bg-muted/20">
                    <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                            <GitBranch className="h-4 w-4 text-primary" />
                        </div>
                        <div className="flex-1 space-y-1">
                            <CardTitle>TransAssist Configuration</CardTitle>
                            <CardDescription>
                                Transfer assistance to other assistants based on context
                            </CardDescription>
                        </div>
                        {destinations.length > 0 && !isReadOnly && (
                            <Button
                                size="sm"
                                onClick={addDestination}
                                className="gap-2"
                            >
                                <Plus className="h-4 w-4" />
                                Add Destination
                            </Button>
                        )}
                    </div>
                </CardHeader>
                <CardContent className="space-y-6">
                    {/* Async Toggle */}
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 flex-1">
                            <div className="flex items-center gap-2">
                                <Zap className="h-4 w-4 text-muted-foreground" />
                                <div className="space-y-1">
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm font-medium">Async Tool</span>
                                        <TooltipProvider delayDuration={200}>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                                                </TooltipTrigger>
                                                <TooltipContent className="max-w-sm p-3">
                                                    <div className="space-y-2">
                                                        <div>
                                                            <p className="text-xs font-medium">Async (enabled):</p>
                                                            <p className="text-xs text-muted-foreground">
                                                                The assistant moves forward immediately without waiting for the transfer to complete. Use this when you just want to trigger the transfer.
                                                            </p>
                                                        </div>
                                                        <div>
                                                            <p className="text-xs font-medium">Sync (disabled):</p>
                                                            <p className="text-xs text-muted-foreground">
                                                                The assistant waits for the transfer to complete before continuing. Use this when you need confirmation of the transfer.
                                                            </p>
                                                        </div>
                                                    </div>
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                    </div>
                                    <p className="text-xs text-muted-foreground">
                                        {isAsync 
                                            ? "Assistant won't wait for transfer confirmation" 
                                            : "Assistant will wait for transfer to complete"}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <Switch
                            checked={isAsync}
                            onCheckedChange={onAsyncChange}
                            disabled={isReadOnly || !onAsyncChange}
                        />
                    </div>

                    {/* Destinations */}
                    {destinations.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 text-center">
                            <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-muted mb-4">
                                <GitBranch className="h-8 w-8 text-muted-foreground" />
                            </div>
                            <h3 className="text-sm font-medium mb-1">No Destinations</h3>
                            <p className="text-sm text-muted-foreground mb-4 max-w-sm">
                                Add destinations where assistance can be transferred based on conversation context
                            </p>
                            {!isReadOnly && (
                                <Button
                                    size="sm"
                                    onClick={addDestination}
                                    className="gap-2"
                                >
                                    <Plus className="h-4 w-4" />
                                    Add Your First Destination
                                </Button>
                            )}
                        </div>
                    ) : (
                        <Accordion
                            type="multiple"
                            value={openAccordion}
                            onValueChange={setOpenAccordion}
                            className="space-y-3"
                        >
                            {destinations.map((dest, index) => (
                                <AccordionItem
                                    key={index}
                                    value={`dest-${index}`}
                                    className="border rounded-md overflow-hidden"
                                >
                                    <AccordionTrigger className="px-4 py-3 hover:bg-muted/50 data-[state=open]:bg-muted/80">
                                        <div className="flex items-center justify-between w-full">
                                            <div className="flex items-center gap-3">
                                                <div className="bg-primary/10 p-1.5 rounded-md">
                                                    {dest.type === 'assistant' ? (
                                                        <Bot className="h-4 w-4 text-primary" />
                                                    ) : (
                                                        <Server className="h-4 w-4 text-primary" />
                                                    )}
                                                </div>
                                                <div className="text-left">
                                                    <h4 className="text-sm font-medium">
                                                        Destination {index + 1}
                                                    </h4>
                                                    {dest.type === 'assistant' && (dest as HandoffDestinationAssistant).assistantId && (
                                                        <p className="text-xs text-muted-foreground">
                                                            {assistants.find(a => a.id === (dest as HandoffDestinationAssistant).assistantId)?.name || 
                                                             (dest as HandoffDestinationAssistant).assistantId?.substring(0, 8) + '...'}
                                                        </p>
                                                    )}
                                                    {dest.type === 'dynamic' && (dest as HandoffDestinationDynamic).server?.url && (
                                                        <p className="text-xs text-muted-foreground truncate max-w-[200px]">
                                                            {(dest as HandoffDestinationDynamic).server?.url}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <div className="bg-muted/50 px-2 py-1 rounded text-xs font-medium capitalize">
                                                    {dest.type}
                                                </div>
                                                {!isReadOnly && (
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="sm"
                                                        className="h-8 w-8 p-0 mr-2 hover:bg-destructive/10 hover:text-destructive"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            removeDestination(index);
                                                        }}
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    </AccordionTrigger>
                                    <AccordionContent className="bg-muted/20 border-t">
                                        <div className="p-4 space-y-6">
                                            {/* Basic Information Card */}
                                            <Card className="shadow-sm">
                                                <CardHeader className="border-b bg-muted/20 py-2.5">
                                                    <div className="flex items-center gap-2">
                                                        <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10">
                                                            <Info className="h-3.5 w-3.5 text-primary" />
                                                        </div>
                                                        <div className="flex-1">
                                                            <h4 className="text-sm font-medium leading-none">Basic Information</h4>
                                                        </div>
                                                    </div>
                                                </CardHeader>
                                                <CardContent className="space-y-4">
                                                    {/* Destination Type */}
                                                    <div className="space-y-2">
                                                        <Label className="flex items-center gap-2 text-sm font-medium">
                                                            <Server className="h-4 w-4 text-primary" />
                                                            Destination Type
                                                            <TooltipProvider delayDuration={200}>
                                                                <Tooltip>
                                                                    <TooltipTrigger asChild>
                                                                        <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                                                                    </TooltipTrigger>
                                                                    <TooltipContent side="right" align="start" className="max-w-sm p-3">
                                                                        <div className="space-y-2">
                                                                            <div>
                                                                                <p className="text-xs font-medium">Assistant:</p>
                                                                                <p className="text-xs text-muted-foreground">
                                                                                    Transfer to a specific assistant in your organization.
                                                                                </p>
                                                                            </div>
                                                                            <div>
                                                                                <p className="text-xs font-medium">Dynamic:</p>
                                                                                <p className="text-xs text-muted-foreground">
                                                                                    Transfer to a destination determined by your server at runtime.
                                                                                </p>
                                                                            </div>
                                                                        </div>
                                                                    </TooltipContent>
                                                                </Tooltip>
                                                            </TooltipProvider>
                                                        </Label>
                                                        <Combobox
                                                            options={destinationTypeOptions}
                                                            value={dest.type}
                                                            onValueChange={(value) => updateDestinationType(index, value as 'assistant' | 'dynamic')}
                                                            placeholder="Select type..."
                                                            disabled={isReadOnly}
                                                        />
                                                    </div>

                                                    {/* Description */}
                                                    <div className="space-y-2">
                                                        <Label className="flex items-center gap-2 text-sm font-medium">
                                                            <MessageSquare className="h-4 w-4 text-primary" />
                                                            Description
                                                            <TooltipProvider delayDuration={200}>
                                                                <Tooltip>
                                                                    <TooltipTrigger asChild>
                                                                        <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                                                                    </TooltipTrigger>
                                                                    <TooltipContent side="right" align="start" className="max-w-sm">
                                                                        <p className="text-xs">
                                                                            This description helps the AI decide when to use this destination based on the conversation context.
                                                                        </p>
                                                                    </TooltipContent>
                                                                </Tooltip>
                                                            </TooltipProvider>
                                                        </Label>
                                                        <Textarea
                                                            className="min-h-[80px] resize-none"
                                                            value={dest.description || ''}
                                                            onChange={(e) => updateDestination(index, { description: e.target.value })}
                                                            placeholder="e.g., Transfer to billing department for payment-related issues"
                                                            disabled={isReadOnly}
                                                        />
                                                    </div>
                                                </CardContent>
                                            </Card>

                                            {/* Assistant-specific Configuration */}
                                            {dest.type === 'assistant' && (
                                                <Card className="shadow-sm">
                                                    <CardHeader className="border-b bg-muted/20 py-2.5">
                                                        <div className="flex items-center gap-2">
                                                            <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10">
                                                                <Bot className="h-3.5 w-3.5 text-primary" />
                                                            </div>
                                                            <div className="flex-1">
                                                                <h4 className="text-sm font-medium leading-none">Assistant Configuration</h4>
                                                            </div>
                                                        </div>
                                                    </CardHeader>
                                                    <CardContent className="space-y-4">
                                                        {/* Select Assistant */}
                                                        <div className="space-y-2">
                                                            <Label className="flex items-center gap-2 text-sm font-medium">
                                                                <Bot className="h-4 w-4 text-primary" />
                                                                Select Assistant
                                                                <TooltipProvider delayDuration={200}>
                                                                    <Tooltip>
                                                                        <TooltipTrigger asChild>
                                                                            <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                                                                        </TooltipTrigger>
                                                                        <TooltipContent side="right" align="start" className="max-w-sm">
                                                                            <p className="text-xs">
                                                                                Select the assistant to transfer the call to. The assistant ID will be automatically set.
                                                                            </p>
                                                                        </TooltipContent>
                                                                    </Tooltip>
                                                                </TooltipProvider>
                                                            </Label>
                                                            {isLoadingAssistants ? (
                                                                <div className="flex items-center gap-2 h-9 px-3 border rounded-md bg-muted/50">
                                                                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                                                                    <span className="text-sm text-muted-foreground">Loading assistants...</span>
                                                                </div>
                                                            ) : (
                                                                <Combobox
                                                                    options={assistantOptions}
                                                                    value={dest.assistantId || ''}
                                                                    onValueChange={(value) => {
                                                                        const selectedAssistant = assistants.find(a => a.id === value);
                                                                        updateDestination(index, { 
                                                                            assistantId: value,
                                                                            assistantName: selectedAssistant?.name || ''
                                                                        });
                                                                    }}
                                                                    placeholder="Select an assistant..."
                                                                    emptyText="No assistants available"
                                                                    searchPlaceholder="Search by name or ID..."
                                                                    disabled={isReadOnly}
                                                                />
                                                            )}
                                                        </div>

                                                        {/* Context Engineering Plan */}
                                                        <div className="space-y-2">
                                                            <Label className="flex items-center gap-2 text-sm font-medium">
                                                                <MessageSquare className="h-4 w-4 text-primary" />
                                                                Context Engineering Plan
                                                                <TooltipProvider delayDuration={200}>
                                                                    <Tooltip>
                                                                        <TooltipTrigger asChild>
                                                                            <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                                                                        </TooltipTrigger>
                                                                        <TooltipContent side="right" align="start" className="max-w-sm p-3">
                                                                            <p className="text-xs mb-2">
                                                                                Controls which conversation messages are passed to the next assistant.
                                                                            </p>
                                                                            <ul className="text-xs space-y-1">
                                                                                <li className="flex gap-2">
                                                                                    <span className="text-muted-foreground/50">•</span>
                                                                                    <span><strong>All:</strong> Pass entire conversation history</span>
                                                                                </li>
                                                                                <li className="flex gap-2">
                                                                                    <span className="text-muted-foreground/50">•</span>
                                                                                    <span><strong>Last N:</strong> Pass only the most recent N messages</span>
                                                                                </li>
                                                                                <li className="flex gap-2">
                                                                                    <span className="text-muted-foreground/50">•</span>
                                                                                    <span><strong>None:</strong> Start fresh with no history</span>
                                                                                </li>
                                                                            </ul>
                                                                        </TooltipContent>
                                                                    </Tooltip>
                                                                </TooltipProvider>
                                                            </Label>
                                                            <Combobox
                                                                options={contextPlanOptions}
                                                                value={dest.contextEngineeringPlan?.type || 'all'}
                                                                onValueChange={(value) => updateContextPlan(index, value as 'all' | 'lastNMessages' | 'none', 10)}
                                                                placeholder="Select context plan..."
                                                                disabled={isReadOnly}
                                                            />
                                                        </div>

                                                        {/* Max Messages for lastNMessages */}
                                                        {dest.contextEngineeringPlan?.type === 'lastNMessages' && (
                                                            <div className="space-y-2">
                                                                <Label className="flex items-center gap-2 text-sm font-medium">
                                                                    <Hash className="h-4 w-4 text-primary" />
                                                                    Maximum Messages
                                                                </Label>
                                                                <NumberInput
                                                                    value={dest.contextEngineeringPlan.maxMessages || 10}
                                                                    onChange={(value) => updateContextPlan(index, 'lastNMessages', value)}
                                                                    min={1}
                                                                    max={100}
                                                                    disabled={isReadOnly}
                                                                />
                                                            </div>
                                                        )}
                                                    </CardContent>
                                                </Card>
                                            )}

                                            {/* Dynamic-specific Configuration */}
                                            {dest.type === 'dynamic' && (
                                                <Card className="shadow-sm">
                                                    <CardHeader className="border-b bg-muted/20 py-2.5">
                                                        <div className="flex items-center gap-2">
                                                            <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10">
                                                                <Server className="h-3.5 w-3.5 text-primary" />
                                                            </div>
                                                            <div className="flex-1">
                                                                <h4 className="text-sm font-medium leading-none">Server Configuration</h4>
                                                            </div>
                                                        </div>
                                                    </CardHeader>
                                                    <CardContent className="space-y-4">
                                                        {/* Server URL */}
                                                        <div className="space-y-2">
                                                            <Label className="flex items-center gap-2 text-sm font-medium">
                                                                <Globe2 className="h-4 w-4 text-primary" />
                                                                Server URL
                                                                <TooltipProvider delayDuration={200}>
                                                                    <Tooltip>
                                                                        <TooltipTrigger asChild>
                                                                            <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                                                                        </TooltipTrigger>
                                                                        <TooltipContent side="right" align="start" className="max-w-md p-3">
                                                                            <p className="text-xs mb-2">
                                                                                Currently, this system is integrated exclusively with Our Workflows in a closed environment. External webhooks or third-party integrations are not supported at this time.
                                                                            </p>
                                                                            <div className="space-y-1">
                                                                                <p className="text-xs font-medium">Valid URL format:</p>
                                                                                <code className="text-xs bg-accent px-1.5 py-1 rounded block break-all">
                                                                                    https://workflow.voicebot.studio/api/v1/webhooks/[ID]
                                                                                </code>
                                                                            </div>
                                                                        </TooltipContent>
                                                                    </Tooltip>
                                                                </TooltipProvider>
                                                            </Label>
                                                            <Input
                                                                className="h-9"
                                                                value={dest.server?.url || ''}
                                                                onChange={(e) => updateServerField(index, 'url', e.target.value)}
                                                                placeholder="https://workflow.voicebot.studio/api/v1/webhooks/[ID]"
                                                                disabled={isReadOnly}
                                                            />
                                                            {dest.server?.url && !isValidWorkflowUrl(dest.server.url) && (
                                                                <div className="flex items-center gap-1.5 text-xs text-destructive">
                                                                    <AlertCircle className="h-3.5 w-3.5" />
                                                                    <span>URL must start with https://workflow.voicebot.studio/</span>
                                                                </div>
                                                            )}
                                                            <p className="text-xs text-muted-foreground">
                                                                This URL will receive handoff destination requests from the system.
                                                            </p>
                                                        </div>

                                                        {/* Timeout */}
                                                        <div className="space-y-2">
                                                            <Label className="flex items-center gap-2 text-sm font-medium">
                                                                <Clock className="h-4 w-4 text-primary" />
                                                                Timeout (seconds)
                                                            </Label>
                                                            <NumberInput
                                                                value={dest.server?.timeoutSeconds || 20}
                                                                onChange={(value) => updateServerField(index, 'timeoutSeconds', value)}
                                                                min={1}
                                                                max={60}
                                                                disabled={isReadOnly}
                                                            />
                                                        </div>

                                                        {/* Server Headers */}
                                                        <div className="space-y-2">
                                                            <Label className="flex items-center gap-2 text-sm font-medium">
                                                                <Key className="h-4 w-4 text-primary" />
                                                                Server Headers (Optional)
                                                                <TooltipProvider delayDuration={200}>
                                                                    <Tooltip>
                                                                        <TooltipTrigger asChild>
                                                                            <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                                                                        </TooltipTrigger>
                                                                        <TooltipContent side="right" align="start" className="max-w-sm">
                                                                            <p className="text-xs">
                                                                                Additional headers to include in the webhook request to your server.
                                                                            </p>
                                                                        </TooltipContent>
                                                                    </Tooltip>
                                                                </TooltipProvider>
                                                            </Label>
                                                            {dest.server?.headers && Object.entries(dest.server.headers).length > 0 ? (
                                                                <div className="space-y-2">
                                                                    {Object.entries(dest.server.headers).map(([key, value]) => (
                                                                        <div key={key} className="flex gap-2">
                                                                            <Input
                                                                                className="h-9 flex-1"
                                                                                value={key}
                                                                                disabled
                                                                                placeholder="Header Key"
                                                                            />
                                                                            <Input
                                                                                className="h-9 flex-1"
                                                                                value={value}
                                                                                onChange={(e) => updateServerHeader(index, key, e.target.value)}
                                                                                disabled={isReadOnly}
                                                                                placeholder="Header Value"
                                                                            />
                                                                            <Button
                                                                                type="button"
                                                                                variant="ghost"
                                                                                size="sm"
                                                                                className="h-9 w-9 p-0"
                                                                                onClick={() => removeServerHeader(index, key)}
                                                                                disabled={isReadOnly}
                                                                            >
                                                                                <Trash2 className="h-4 w-4" />
                                                                            </Button>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            ) : (
                                                                <div className="flex items-center justify-center py-4 text-sm text-muted-foreground border-2 border-dashed rounded-md">
                                                                    No headers configured
                                                                </div>
                                                            )}
                                                            <Button
                                                                type="button"
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={() => {
                                                                    const newKey = `header_${Date.now()}`;
                                                                    updateServerHeader(index, newKey, '');
                                                                }}
                                                                disabled={isReadOnly}
                                                                className="gap-2"
                                                            >
                                                                <Plus className="h-4 w-4" />
                                                                Add Header
                                                            </Button>
                                                        </div>
                                                    </CardContent>
                                                </Card>
                                            )}
                                        </div>
                                    </AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    )}
                </CardContent>
            </Card>

            {/* Best Practices Card */}
            <Card className="shadow-sm border-dashed">
                <CardHeader className="border-b bg-muted/20 py-2.5">
                    <div className="flex items-center gap-2">
                        <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10">
                            <Lightbulb className="h-3.5 w-3.5 text-primary" />
                        </div>
                        <div className="flex-1">
                            <h4 className="text-sm font-medium leading-none">Configuration Tips</h4>
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="space-y-2">
                    <ul className="text-xs text-muted-foreground space-y-1.5">
                        <li className="flex gap-2">
                            <span className="text-muted-foreground/50">•</span>
                            <span>Write detailed descriptions to help the AI decide which destination fits the conversation context</span>
                        </li>
                        <li className="flex gap-2">
                            <span className="text-muted-foreground/50">•</span>
                            <span>Select assistants by ID from the dropdown to ensure accurate routing</span>
                        </li>
                        <li className="flex gap-2">
                            <span className="text-muted-foreground/50">•</span>
                            <span>Configure context engineering based on whether the receiving assistant needs conversation history</span>
                        </li>
                        <li className="flex gap-2">
                            <span className="text-muted-foreground/50">•</span>
                            <span>Keep all messages for seamless conversation flow, use last N for relevant context only, or choose none for isolated interactions</span>
                        </li>
                        <li className="flex gap-2">
                            <span className="text-muted-foreground/50">•</span>
                            <span>Set appropriate timeouts for dynamic destinations to avoid delays in call flow</span>
                        </li>
                        <li className="flex gap-2">
                            <span className="text-muted-foreground/50">•</span>
                            <span>Always validate your transfer configurations with test calls before deploying to production</span>
                        </li>
                    </ul>
                </CardContent>
            </Card>
        </motion.div>
    );
}
